/* eslint-disable react-hooks/exhaustive-deps */
import { debounce } from 'lodash';
import { filterSelectOptions } from 'datastake-daf/src/helpers/Forms';
import { SelectFiltersTimeFrame } from 'datastake-daf/dist/components';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatClassname } from 'datastake-daf/src/helpers/ClassesHelper';
import { Radio, Select, Space, Form, Input, Button, DatePicker } from 'antd';

export default function ModalFilters({
  initialFilters = [],
  setFiltersVisible = () => { },
  filtersVisible = false,
  clearFilters = false,
  setClearFilters = () => { },
  t = (t) => t,
  onSubmit = null,
  activeFiltersInit = {},
  className,
}) {

  const [texts, setTexts] = useState({});
  const [filters, setFilters] = useState([]);
  const [initFilters, setInitFilters] = useState(activeFiltersInit || {})
  const [activeFilters, setActiveFilters] = useState(activeFiltersInit);

  const initializeFilters = useCallback((clear) => {
    if (clear) {
      setActiveFilters({});
      setTexts({});
    }

    if (filters.length) {
      setFilters((filters) => filters.map((f) => {
        const initialFilter = initialFilters.find((i) => i.key === f.key);
        if (initialFilter && f) {
          if (clear) {
            return initialFilter;
          }
          if (f.value) {
            return { ...initialFilter, value: f.value };
          }
        }
        return initialFilter || {};
      }));
    } else {
      setFilters(initialFilters.map((f) => ({
        ...f,
        value: f.value || activeFiltersInit[f.key],
      })));
      setTexts({});
    }
  }, [initialFilters, filters, activeFiltersInit]);

  useEffect(() => {
    initializeFilters();
  }, [initialFilters]);

  const handleChange = useCallback((e, contIndex, type) => {
    const _new = filters.map((container, i) => {
      if (i === contIndex) {
        if (e && e.target) {
          const value = e.target.value;
          return { ...container, value };
        }
        if (type === 'select' || type === 'timeframe' || type === 'textInput') {
          return { ...container, value: e };
        }
      }
      return container;
    });

    const activeFilters = {};
    _new.forEach((filter) => {

      if (filter.type === 'radioGroup' || filter.type === 'select' || filter.type === 'textInput' || filter.type === 'timeframe') {
        if (filter.value) {
          activeFilters[filter.key] = filter.value;
        }
      }
    });

    setActiveFilters(activeFilters);

    setFilters(_new);
  }, [filters]);

  const applyFiltersHandler = useCallback(() => {
    const activeFilters = {};
    filters.forEach((filter) => {
      if (filter.type === 'radioGroup' || filter.type === 'select' || filter.type === 'textInput' || filter.type === 'timeframe') {
        if (filter.value) {
          activeFilters[filter.key] = filter.value;
        }
      }
    });

    setActiveFilters(activeFilters);
    setFiltersVisible(false);
  }, [filters]);

  const clearFiltersHandler = useCallback(() => {
    initializeFilters(true);
  }, [initialFilters, initializeFilters]);

  useEffect(() => {
    if (clearFilters) {
      clearFiltersHandler();
      setClearFilters(false);
    }
  }, [clearFilters]);

  const componentOnSearch = useMemo(() => debounce(handleChange, 400), [handleChange]);

  const renderContainer = (container, i) => {
    switch (container.type) {
      case 'radioGroup':
        return (
          <div className="filter-container flex-1" key={i.toString()}>
            <label>{t(container.label || '...')}</label>
            <Form.Item>
              <Radio.Group
                value={container.value}
                onChange={(val) => handleChange(val, i, 'radio')}
                defaultValue={container.defaultValue}
              >
                <Space direction="vertical">
                  {container.items.map((item, j) => (
                    <Radio key={j.toString()} value={item.value}>
                      {t(item.label)}
                    </Radio>
                  ))}
                </Space>
              </Radio.Group>
            </Form.Item>
          </div>
        );
      case 'select':
        let itemsToMap;

        if (container.showIfHasValue) {
          const f = filters.find((f) => f.key === container.showIfHasValue);

          if (f) {
            if (!f.value) {
              if (container.value) {
                setFilters((prev) => prev.map((p) => {
                  if (p.key === container.key) {
                    return { ...p, value: undefined };
                  }
                  return p;
                }));
              }
              return null;
            }

            itemsToMap = container.items.filter((v) => v.group === f.value);

            if (container.value) {
              const activeGroup = container.items.find((c) => c.value === container.value).group;
              if (activeGroup !== f.value) {
                setFilters((prev) => prev.map((p) => {
                  if (p.key === container.key) {
                    return { ...p, value: undefined };
                  }
                  return p;
                }));
              }
            }
          }
        }

        const _items = typeof container.renderItems === 'function'
          ? container.renderItems(activeFilters)
          : (itemsToMap || container.items);

        return (
          <div className="filter-container flex-1" key={i.toString()}>
            <label>{t(container.label || '...')}</label>
            {!!_items && (
              <Form.Item>
                <Select
                  showSearch
                  placeholder={t(container.placeholder)}
                  defaultValue={container.defaultValue}
                  value={container.value}
                  filterOption={filterSelectOptions}
                  onChange={(val) => handleChange(val, i, 'select')}
                  allowClear
                >
                  {_items.map((c) => (
                    <Select.Option value={c.value} key={c.value}>
                      {t(c.label)}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </div>
        );
      case 'timeframe':
        return (
          <div className="filter-container flex-1" key={i.toString()}>
            <label>{t(container.label || '...')}</label>
            <SelectFiltersTimeFrame
              t={t}
              key={i.toString()}
              value={texts[i]}
              onChange={(e) => {
                setTexts({ ...texts, [i.toString()]: e });
                componentOnSearch(e, i, 'timeframe');
              }}
            />
          </div>
        )
      case 'date':
        return (
          <div className="filter-container flex-1" key={i.toString()}>
            <label>{t(container.label || '...')}</label>
            <Form.Item>
              <DatePicker
                value={texts[i] || ''}
                format="DD MM YYYY"
                onChange={(e) => {
                  setTexts({ ...texts, [i.toString()]: e });
                  componentOnSearch(e, i, 'date');
                }}
                placeholder={t(container.placeholder)}
              />
            </Form.Item>
          </div>
        );
      case 'textInput':
        return (
          <div className="filter-container flex-1" key={i.toString()}>
            <label>{t(container.label || '...')}</label>
            <Form.Item>
              <Input
                value={texts[i] || ''}
                onChange={(e) => {
                  setTexts({ ...texts, [i.toString()]: e.target.value });
                  componentOnSearch(e.target.value, i, 'textInput');
                }}
                placeholder={t(container.placeholder)}
              />
            </Form.Item>
          </div>
        );
      default:
        return null;
    }
  };

  const hasChanges = useMemo(() => {
    let _hasChanges = false;
    const activeFiltersKeys = Object.keys(activeFilters)

    if (activeFiltersKeys.length !== Object.keys(initFilters).length) {
      return true;
    }

    activeFiltersKeys.forEach((k) => {
      if (!_hasChanges && initFilters[k] !== activeFilters[k]) {
        _hasChanges = true;
      }
    })

    return _hasChanges;
  }, [initFilters, filters]);

  const renderFilters = () => (
    <Form
      name="filters"
      onFinish={applyFiltersHandler}
    >
      <div className={formatClassname([className, 'daf-select-filters'])}>
        <div className="filters px-0">
          <div className="filters-cont">
            {filters.map((container, i) => renderContainer(container, i))}
            <div className="cont apply-cont">
              <Button
                type={hasChanges ? 'primary' : 'default'}
                onClick={() => {
                  if (hasChanges) {
                    setInitFilters(activeFilters);
                    onSubmit(activeFilters);
                  } else {
                    setInitFilters({});
                    onSubmit({});
                    initializeFilters(true);
                  }
                }}
              >
                {t(hasChanges ? 'Apply Filters' : 'Cancel')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Form>
  )

  if (filtersVisible) {
    return renderFilters();
  }

  return null
}
